import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import listViewPlugin from "@fullcalendar/list"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin, {
  EventResizeDoneArg,
} from "@fullcalendar/interaction"
import enAuLocale from "@fullcalendar/core/locales/en-au"
import { ErrorMessage } from "@hookform/error-message"
import { MinusCircle } from "lucide-react"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CalendarApi } from "@fullcalendar/core/index.js"
import { useId, useState } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { ErrorHint } from "@/components/ui/errorhint"
import { Label } from "@/components/ui/label"
import { PageBody, TestId } from "@/components/ui/layout"
import { Textarea } from "@/components/ui/textarea"
import { VALIDATION_ERROR_KEYS } from "@/i18n"
import { SelectInput } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { useFullCalendarApi } from "@/lib/fullCalendar"
import { EducatorsCustomClassesTeacherClassListingAvailabilityForm } from "@/types/serializers"
import { SagecoolCalendar } from "@/components/shared/sagecool-calendar"
import { SaveButton } from "@/components/shared/action-buttons"
import { RHFDirtyDraftRegistration } from "@/components/shared/dirty-draft-navigation"
import { useNavigate, useProps } from "@/lib/page-rendering"

type AvailabilityTime = {
  start_time: string
  end_time: string
}
type AvailabilitySchema = {
  monday: Array<AvailabilityTime>
  tuesday: Array<AvailabilityTime>
  wednesday: Array<AvailabilityTime>
  thursday: Array<AvailabilityTime>
  friday: Array<AvailabilityTime>
  saturday: Array<AvailabilityTime>
  sunday: Array<AvailabilityTime>
}

type TeacherClassListingAvailabilityFormValues =
  EducatorsCustomClassesTeacherClassListingAvailabilityForm & {
    availability: AvailabilitySchema
  }

export default function Edit() {
  const navigate = useNavigate()
  const { teacher_class_listing_availability, class_name } = useProps<{
    teacher_class_listing_availability: TeacherClassListingAvailabilityFormValues
    class_name: string
  }>()
  const { t } = useTranslation([
    "teacher_class_listing_availabilities",
    "classes",
  ])
  const methods = useForm<TeacherClassListingAvailabilityFormValues>({
    defaultValues: teacher_class_listing_availability,
  })

  const { errors } = methods.formState

  const formId = useId()
  const titleId = useId()

  return (
    <>
      <EducatorPageChrome
        records={{ "educators/classes/edit": class_name }}
        titleId={titleId}
        action={<SaveButton form={formId} />}
      />
      <PageBody>
        <Card>
          <CardHeader></CardHeader>
          <CardContent>
            <FormProvider {...methods}>
              <RHFDirtyDraftRegistration />
              <form
                id={formId}
                onSubmit={methods.handleSubmit((data) =>
                  navigate({
                    href: ".",
                    method: "put",
                    data: { teacher_class_listing_availability: data },
                  }),
                )}
                aria-labelledby={titleId}
                className="flex flex-col gap-8"
                data-testid="availability-form"
              >
                <Controller
                  name="availability"
                  control={methods.control}
                  render={({ field }) => (
                    <AvailabilityCalendar
                      onAvailabilityChange={field.onChange}
                      availability={field.value}
                    />
                  )}
                />
                <div className="flex w-full max-w-4xl flex-col">
                  <Label
                    htmlFor="availability_notes"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    {t(
                      "teacher_class_listing_availabilities:form.availability.availability_notes",
                    )}
                  </Label>
                  <Textarea
                    id="availability_notes"
                    autoComplete="off"
                    {...methods.register("availability_notes")}
                  />
                  <ErrorMessage
                    name="availability_notes"
                    errors={errors}
                    render={ErrorHint}
                  />
                </div>
              </form>
            </FormProvider>
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}

const currentLocale = enAuLocale as unknown as { week: { dow: number } }

const daysOfWeek = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const

function createCalendarEventFromAvailabilityTimeFn(calendarApi: CalendarApi) {
  const activeStart = calendarApi.view.activeStart
    .toTemporalInstant()
    .toZonedDateTimeISO(Temporal.Now.timeZoneId())

  const calendarDates = Object.fromEntries(
    daysOfWeek.map((day, index) => [
      day,
      activeStart.add({ days: (7 + index - currentLocale.week.dow) % 7 }),
    ]),
  )

  const calendarDateWithTime = (
    date: Temporal.PlainDateTime,
    timeString: string,
  ) => {
    const time = Temporal.PlainTime.from(timeString)
    return date.with({ hour: time.hour, minute: time.minute })
  }
  return (
    start_time: string,
    end_time: string,
    day: (typeof daysOfWeek)[number],
  ) => {
    const start = calendarDateWithTime(
      calendarDates[day].toPlainDateTime(),
      start_time,
    )
    const end = calendarDateWithTime(
      calendarDates[day].toPlainDateTime(),
      end_time,
    )
    return {
      start: new Date(start.toString()),
      end: new Date(end.toString()),
      id: crypto.randomUUID(),
    }
  }
}

function getCalendarEventsFromAvailability(
  api: CalendarApi,
  availability: TeacherClassListingAvailabilityFormValues["availability"],
) {
  const calendarEventFromAvailabilityTime =
    createCalendarEventFromAvailabilityTimeFn(api)

  return daysOfWeek.flatMap((day) => {
    return availability[day].map((availabilityTime) =>
      calendarEventFromAvailabilityTime(
        availabilityTime.start_time,
        availabilityTime.end_time,
        day,
      ),
    )
  })
}

type AvailabilityValues = {
  day: (typeof daysOfWeek)[number] | null
  start_time: string
  end_time: string
}

function AvailabilityTimeInputs({
  calendarApi,
}: {
  calendarApi: CalendarApi | null
}) {
  const { t } = useTranslation([
    "teacher_class_listing_availabilities",
    "common",
  ])
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm<AvailabilityValues>()

  const [error, setError] = useState<string | null>(null)

  if (!calendarApi) return null
  const onSubmit = (data: AvailabilityValues) => {
    const calendarEventFromAvailabilityTime =
      createCalendarEventFromAvailabilityTimeFn(calendarApi)
    const newEvent = calendarEventFromAvailabilityTime(
      data.start_time,
      data.end_time,
      data.day!,
    )

    const isOverlapping = (event: { start: Date | null; end: Date | null }) =>
      (newEvent.start < event.end! && newEvent.end > event.start!) ||
      newEvent.start == event.start ||
      newEvent.end == event.end

    if (calendarApi.getEvents().some(isOverlapping)) {
      setError(
        t(
          "teacher_class_listing_availabilities:form.availability.overlapping_error",
        ),
      )
      return
    }

    if (newEvent.start >= newEvent.end) {
      setError(
        t(
          "teacher_class_listing_availabilities:form.availability.invalid_error",
        ),
      )
      return
    }

    calendarApi.addEvent(newEvent)

    setError(null)
    setValue("start_time", "")
    setValue("end_time", "")
  }

  return (
    <div
      className="flex flex-col gap-6 rounded-md border p-4"
      data-testid="add-availability-form"
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col">
          <Label
            htmlFor="day"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            {t("teacher_class_listing_availabilities:form.availability.day")}
          </Label>
          <Controller
            name="day"
            control={control}
            rules={{ required: t(VALIDATION_ERROR_KEYS.REQUIRED) }}
            render={({ field }) => (
              <SelectInput
                id="day"
                className="w-full"
                value={field.value?.toString()}
                onChange={field.onChange}
                options={daysOfWeek.map((day) => ({
                  value: day,
                  label: t(
                    `teacher_class_listing_availabilities:form.availability.${day}`,
                  ),
                }))}
              />
            )}
          />
          <ErrorMessage name="day" errors={errors} render={ErrorHint} />
        </div>
        <div className="grid w-full grid-cols-2 gap-2">
          <div className="flex flex-col">
            <Label
              htmlFor="start_time"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              {t(
                "teacher_class_listing_availabilities:form.availability.start_time",
              )}
            </Label>
            <Input
              className="w-fit"
              type="time"
              id={"start_time"}
              role="textbox"
              defaultValue={"09:00:00"}
              {...register(`start_time`, {
                required: t(VALIDATION_ERROR_KEYS.REQUIRED),
              })}
            />
            <ErrorMessage name="day" errors={errors} render={ErrorHint} />
          </div>
          <div className="flex flex-col">
            <Label
              htmlFor="end_time"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              {t(
                "teacher_class_listing_availabilities:form.availability.end_time",
              )}
            </Label>
            <Input
              className="w-fit"
              type="time"
              id={"end_time"}
              role="textbox"
              defaultValue={"17:00:00"}
              {...register("end_time", {
                required: t(VALIDATION_ERROR_KEYS.REQUIRED),
              })}
            />
            <ErrorMessage name="day" errors={errors} render={ErrorHint} />
          </div>
        </div>
        <div className="mt-2 flex flex-col pb-8">
          <Button type="button" onClick={handleSubmit(onSubmit)}>
            Add Availability
          </Button>
          {error && <ErrorHint message={error} />}
        </div>
      </div>
    </div>
  )
}

function AvailabilityCalendar({
  availability,
  onAvailabilityChange,
}: {
  availability?: TeacherClassListingAvailabilityFormValues["availability"]
  onAvailabilityChange: (
    availability: TeacherClassListingAvailabilityFormValues["availability"],
  ) => void
}) {
  const { t } = useTranslation([
    "teacher_class_listing_availabilities",
    "classes",
  ])
  const dummyNow = new Date()

  const [calendarRef, calendarApi] = useFullCalendarApi("AvailabilityCalendar")
  const [eventListRef, eventListApi] = useFullCalendarApi(
    "AvailabilityEventList",
  )

  const events =
    (calendarApi &&
      availability &&
      getCalendarEventsFromAvailability(calendarApi, availability)) ||
    []

  const handleChange = () => {
    const events = calendarApi?.getEvents() || []
    const fromEntries: <T extends readonly [keyof any, any][]>(
      entries: T,
    ) => T extends T ? { [E in T[number] as E[0]]: E[1] } : never =
      Object.fromEntries

    const calendarDateTimeToTimeString = (date: Date) =>
      `${date.getHours().toString().padStart(2, "0")}:${date
        .getMinutes()
        .toString()
        .padStart(2, "0")}`

    onAvailabilityChange(
      fromEntries(
        daysOfWeek.map((day) => [
          day,
          events
            .filter((event) => event.start!.getDay() == daysOfWeek.indexOf(day))
            .map((event) => ({
              start_time: calendarDateTimeToTimeString(event.start!),
              end_time: calendarDateTimeToTimeString(event.end!),
            })),
        ]),
      ),
    )
  }

  const handleSelect = (selectInfo: { start: Date; end: Date }) => {
    if (!calendarApi) return
    if (selectInfo.start.getDay() != selectInfo.end.getDay()) return

    calendarApi.addEvent({
      id: crypto.randomUUID(),
      start: selectInfo.start,
      end: selectInfo.end,
    })
  }

  const handleResize = (resizeInfo: EventResizeDoneArg) => {
    if (!resizeInfo.event.start || !resizeInfo.event.end) return

    if (resizeInfo.event.start.getDay() != resizeInfo.event.end.getDay()) {
      resizeInfo.revert()
      return
    }
  }

  const calandarEventContent = ({
    event,
  }: {
    event: { startStr: string; endStr: string }
  }) => {
    const parseTime = (time: string) =>
      Temporal.Instant.from(time).toZonedDateTimeISO(Temporal.Now.timeZoneId())
    const start = parseTime(event.startStr)
    const end = parseTime(event.endStr)

    return (
      <TestId testid="calendar-item">
        <p>
          {start.toLocaleString("en-AU", { weekday: "long" })}{" "}
          {start.toLocaleString("en-AU", {
            hour: "numeric",
            minute: "2-digit",
          })}{" "}
          -{" "}
          {end.toLocaleString("en-AU", { hour: "numeric", minute: "2-digit" })}
        </p>
      </TestId>
    )
  }

  const listEventContent = ({ event }: { event: any }) => {
    return (
      <Button
        variant="link-destructive"
        type="button"
        data-testid={`remove-${event.id}`}
        onClick={() => {
          calendarApi!.getEventById(event.id)?.remove()
          eventListApi!.refetchEvents()
        }}
      >
        <MinusCircle />
        Remove
      </Button>
    )
  }

  return (
    <div className="flex flex-row gap-4">
      <div
        className="h-full grow"
        style={{ "--fc-today-bg-color": "transparent" } as React.CSSProperties}
      >
        <TestId testid="availability-week-calendar">
          <SagecoolCalendar
            locale={enAuLocale}
            plugins={[timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            headerToolbar={false}
            views={{
              timeGridWeek: {
                type: "timeGrid",
                duration: { weeks: 1 },
                titleFormat: { week: "short" },
              },
            }}
            select={handleSelect}
            eventResize={handleResize}
            selectAllow={(selectInfo) =>
              selectInfo.start.getDay() == selectInfo.end.getDay()
            }
            eventOverlap={false}
            selectOverlap={false}
            eventStartEditable={false}
            unselectAuto={true}
            eventResizableFromStart={true}
            editable={true}
            selectable={true}
            selectMirror={true}
            allDaySlot={false}
            weekends={true}
            dayHeaderFormat={{ weekday: "long" }}
            now={dummyNow}
            nowIndicator={false}
            ref={calendarRef}
            eventContent={calandarEventContent}
            eventAdd={handleChange}
            eventChange={handleChange}
            eventRemove={handleChange}
            events={events}
          />
        </TestId>
      </div>
      <div className="flex w-96 flex-col gap-6">
        <AvailabilityTimeInputs calendarApi={calendarApi} />
        <TestId testid="availability-list">
          <SagecoolCalendar
            locale={enAuLocale}
            noEventsClassNames={["p-4", "text-sm"]}
            viewClassNames={["rounded-md"]}
            plugins={[listViewPlugin]}
            initialView="listWeek"
            headerToolbar={false}
            ref={eventListRef}
            height="auto"
            listDaySideFormat={false}
            events={events}
            eventContent={listEventContent}
            noEventsText={t(
              "teacher_class_listing_availabilities:form.availability.no_availability",
            )}
            eventDidMount={(event) => {
              event.el.classList.add("flex", "flex-row", "justify-between")
              event.el
                .querySelector(".fc-list-event-time")
                ?.classList.add("grow", "flex", "flex-row", "items-center")
              event.el
                .querySelector(".fc-list-event-graphic")
                ?.classList.add("hidden")
            }}
          />
        </TestId>
      </div>
    </div>
  )
}
